import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {localizeKey, localizeArt, localizeGenres} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import LinkInput from './LinkInput';
import SelectableTags from './SelectableTags';
import ShareButton from './ShareButton';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import ArtPage from './ArtPage';
// import ArtThumbnail from './ArtThumbnail';
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";
import { saveStateAndScroll, restoreStateAndScroll } from './../utils/stateManager';
import ExpandableImage from './ExpandableImage';
import 'delayed-scroll-restoration-polyfill';
import ArtThumbnail from './ArtThumbnail.js';
import TextUserPair from './TextUserPair.js';
import { formatYear } from './../utils.js'; // Import the global function
import {
  ActionIcon,
  Button,
  Group,
  Image,
  Paper,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import CardEventArtChatMini from './CardEventArtChatMini.js';
import LogbookHistory from './LogbookHistory.js';
import UserReviewsList from './UserReviewsList.js';
import PageHeader from './PageHeader.js';


const PersonalArtEvents = ({eventType, userId, repeatedEnter = 0, onEventTypeChange = null}) => {
  const navigate = useNavigate();
  const [openingDirectChat, setOpeningDirectChat] = useState(false);
  // Three-tab surface on /users/:id/likes (Library / Logbook / Reviews).
  // Only active for eventType === 'like'; the recommendation views
  // keep their plain layout for now. Tab is mirrored to ?tab=... via
  // replaceState so a copied URL restores the same tab.
  const [tab, setTab] = useState(() => {
    if (typeof window === 'undefined') return 'library';
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'logbook' || t === 'reviews' ? t : 'library';
  });
  const setTabAndUrl = (next) => {
    setTab(next);
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (next === 'library') params.delete('tab');
    else params.set('tab', next);
    const qs = params.toString();
    const url = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState({}, '', url);
  };
  const showLibraryContent = eventType !== 'like' || tab === 'library';
  const tabsSwipeRef = useRef(null);
  // Cache Reviews-tab fetch across tab remounts.
  const [reviewsCache, setReviewsCache] = useState(null);
  // Flips true one tick AFTER the grid paints so the LogbookHistory
  // pre-warm requests don't race the LCP-critical /artEvents response
  // and its poster images.
  const [logbookPrewarmReady, setLogbookPrewarmReady] = useState(false);

  // Swipe between Library / Logbook / Reviews. Touch on mobile,
  // mouse-drag on desktop. Attaches to window for touch/mouse-up so a
  // swipe still registers if the finger lifts outside the container.
  // No pointercancel handler — mobile browsers fire it too eagerly
  // and would wipe the start-point mid-swipe.
  useEffect(() => {
    if (eventType !== 'like') return undefined;
    const el = tabsSwipeRef.current;
    if (!el) return undefined;
    const TABS = ['library', 'logbook', 'reviews'];
    const THRESHOLD = 50;
    const DOMINANCE = 1.2;
    let startX = null;
    let startY = null;

    const startSwipe = (x, y, target) => {
      // Cancel only for the tag-chip strip / tab bar — elements that
      // actually have horizontal-scroll content right now.
      let cur = target;
      while (cur && cur !== el && cur.nodeType === 1) {
        if (cur.scrollWidth > cur.clientWidth + 2) {
          const cs = window.getComputedStyle(cur);
          if (cs.overflowX === 'auto' || cs.overflowX === 'scroll') {
            return;
          }
        }
        cur = cur.parentElement;
      }
      startX = x;
      startY = y;
    };
    const endSwipe = (x, y) => {
      if (startX === null) return;
      const dx = x - startX;
      const dy = y - startY;
      startX = null;
      startY = null;
      if (Math.abs(dx) < THRESHOLD) return;
      if (Math.abs(dx) < Math.abs(dy) * DOMINANCE) return;
      setTab((prev) => {
        const idx = TABS.indexOf(prev);
        let next = prev;
        if (dx < 0 && idx < TABS.length - 1) next = TABS[idx + 1];
        else if (dx > 0 && idx > 0) next = TABS[idx - 1];
        if (next !== prev && typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          if (next === 'library') params.delete('tab');
          else params.set('tab', next);
          const qs = params.toString();
          window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
        }
        return next;
      });
    };

    const onTouchStart = (e) => {
      const t = e.changedTouches[0];
      startSwipe(t.clientX, t.clientY, e.target);
    };
    const onTouchEnd = (e) => {
      const t = e.changedTouches[0];
      endSwipe(t.clientX, t.clientY);
    };
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      startSwipe(e.clientX, e.clientY, e.target);
    };
    const onMouseUp = (e) => {
      endSwipe(e.clientX, e.clientY);
    };

    // Start on the container (so we know the swipe originated here),
    // end on window (so a finger/mouse lifted anywhere still counts).
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [eventType]);

  const openDirectChatWith = (otherUserId) => {
    if (openingDirectChat) return;
    setOpeningDirectChat(true);
    axiosInstance
      .post('/directChats', { otherUserId })
      .then((res) => {
        const { chatId, otherUserId: returnedOtherUserId } = res.data || {};
        if (chatId) {
          navigate(`/directChats/${chatId}?with=${returnedOtherUserId || otherUserId}`);
        }
      })
      .catch((err) => {
        console.error('Failed to open direct chat:', err);
      })
      .finally(() => setOpeningDirectChat(false));
  };

  const myUser = useContext(MyUserContext);
  const [finishedInput, setFinishedInput] = useState("");
  const [finishedEvents, setFinishedEvents] = useState(null);

  const [hasAccess, setHasAccess] = useState(true);
  const [followersCount, setFollowersCount] = useState(null);
  const [followeesCount, setFolloweesCount] = useState(null);

  // Initialize from URL `?q=` so a shared URL preserves the active search.
  const [artNameTerm, setArtNameTerm] = useState(() => {
    return new URLSearchParams(window.location.search).get('q') || '';
  });

  const { pathUserId } = useParams();
  const effectiveUserId = () => {
    // UserID from props or path param.
    let effectiveUserId = userId;
    if (!effectiveUserId) {
      effectiveUserId = pathUserId;
    }
    return effectiveUserId;
  }

  useEffect(() => {
    if (finishedEvents === null) {
      setLogbookPrewarmReady(false);
      return undefined;
    }
    const id = setTimeout(() => setLogbookPrewarmReady(true), 0);
    return () => clearTimeout(id);
  }, [finishedEvents]);

  // Preload the Reviews tab in the background so switching to it paints
  // instantly. Gated on finishedEvents so it fires only AFTER the
  // LCP-critical /artEvents request returns — otherwise it competes with
  // it for server/network capacity.
  useEffect(() => {
    if (eventType !== 'like') return undefined;
    const uid = userId || pathUserId;
    if (!uid) return undefined;
    if (finishedEvents === null) return undefined;
    if (reviewsCache !== null) return undefined;
    let cancelled = false;
    axiosInstance
      .get(`/users/${uid}/reviews`)
      .then((res) => { if (!cancelled) setReviewsCache(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setReviewsCache([]); });
    return () => { cancelled = true; };
  }, [eventType, userId, pathUserId, finishedEvents, reviewsCache]);

  const alreadySelectedTagIds = new URLSearchParams(window.location.search).getAll('tags');

  const alreadySelectedTagIdsParam = alreadySelectedTagIds.map((t) => { return "tags="+t}).join("&");
  const initEventsFetchUrl = `/artEvents/${effectiveUserId()}/${eventType}?${alreadySelectedTagIdsParam}&artNameTerm=${encodeURIComponent(artNameTerm)}`;

  // Reflect the search term in the current URL as `?q=...` so a copied URL can be shared.
  // Uses replaceState to avoid polluting browser history on every keystroke.
  const syncSearchTermToURL = (term) => {
    const params = new URLSearchParams(window.location.search);
    if (term) {
      params.set('q', term);
    } else {
      params.delete('q');
    }
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  };
  const [nextUrl, setNextUrl] = useState(initEventsFetchUrl);
  const [loading, setLoading] = useState(false);
  const observer = useRef();
  const latestRequestIdRef = useRef(0);

  const searchInputRef = useRef(null);
  const [shouldFocus, setShouldFocus] = useState(false);
  const [gridClass, setGridClass] = useState('grid-cols-4');
  const [openGlobalManualTagEventId, setOpenGlobalManualTagEventId] = useState(null);
  const [selectedGridItemIdx, setSelectedGridItemIdx] = useState(null);
  const [gridArtDetails, setGridArtDetails] = useState({});
  const [isCopyLinkCopied, setIsCopyLinkCopied] = useState(false);
  const copyLinkTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (copyLinkTimeoutRef.current) clearTimeout(copyLinkTimeoutRef.current);
  }, []);
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopyLinkCopied(true);
      if (copyLinkTimeoutRef.current) clearTimeout(copyLinkTimeoutRef.current);
      copyLinkTimeoutRef.current = setTimeout(() => setIsCopyLinkCopied(false), 800);
    } catch (error) {
      console.error(error);
    }
  };

  // PageHeader ⋯ dropdown state.
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  useEffect(() => {
    if (!pageMenuOpen) return undefined;
    const onDown = (e) => {
      if (e.target.closest && e.target.closest('[data-page-menu]')) return;
      setPageMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [pageMenuOpen]);

  const scrollDetailIntoView = (el) => {
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        window.scrollBy({ top: rect.bottom - window.innerHeight + 60, behavior: 'smooth' });
      }
    });
  };

  // Batch-fetch descriptions when list loads or grows.
  // The backend caps each request at 100 ids ("ids: 1-100 required"), so chunk.
  useEffect(() => {
    if (!finishedEvents || finishedEvents.length === 0) return;
    const ids = [...new Set(finishedEvents.map(item => item.art.id))]
      .filter(id => !gridArtDetails[id]);
    if (ids.length === 0) return;
    const chunks = [];
    for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
    Promise.all(chunks.map(chunk => axiosInstance.post('/arts/descriptions', { ids: chunk })))
      .then((responses) => {
        const merged = responses.reduce((acc, r) => Object.assign(acc, r.data), {});
        setGridArtDetails(prev => ({ ...prev, ...merged }));
      })
      .catch((error) => console.error('Failed to fetch art descriptions:', error));
  }, [finishedEvents]);

  const handleSelectGridItem = (idx) => {
    // Plain toggle — same behavior as the AddEvent trending grid.
    // First click opens the quick-look card; clicking the same tile
    // again hides it.
    setSelectedGridItemIdx(selectedGridItemIdx === idx ? null : idx);
  };

  const globalManualTags = ["like", "veryGood", "good", "finished", "bad", "noRating", "inProgress", "todo"];

  const toggleGridCols = () => {
    setGridClass((prevClass) => (prevClass === 'grid-cols-4' ? 'grid-cols-6' : 'grid-cols-4'));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openGlobalManualTagEventId) {
        return;
      }
      const menuSelector = `[data-global-tag-menu-id="${openGlobalManualTagEventId}"]`;
      if (!event.target.closest(menuSelector)) {
        setOpenGlobalManualTagEventId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openGlobalManualTagEventId]);

  const prevRepeatedEnterRef = useRef(repeatedEnter);
  useEffect(() => {
    if (repeatedEnter !== prevRepeatedEnterRef.current) {
      prevRepeatedEnterRef.current = repeatedEnter;
      setShouldFocus(true);
    }
  }, [repeatedEnter]);

  useEffect(() => {
    if (searchInputRef.current) {
      if (shouldFocus) {
        searchInputRef.current.focus();
      } else {
        searchInputRef.current.blur();
      }
    }
  }, [shouldFocus]);

  useEffect(() => {
    // Enable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    setFinishedEvents(null);
    setCurUser(null);
    setNextUrl(`/artEvents/${effectiveUserId()}/${eventType}?${alreadySelectedTagIdsParam}&artNameTerm=${encodeURIComponent(artNameTerm)}`);
    setFetching(false);
    setHasAccess(true);
    setFollowersCount(null);
    setFolloweesCount(null);
    setReviewsCache(null);
    initialFetchDoneRef.current = false;
  }, [pathUserId, eventType]);

  // TODO: use this to save state and scroll position when navigating away
  // useEffect(() => {
  //   // Restore state and scroll when navigating back
  //   restoreStateAndScroll((restoredState) => {
  //     if (restoredState) {
  //       setFinishedEvents(restoredState.finishedEvents || null);
  //       setArtNameTerm(restoredState.artNameTerm || '');
  //       setNextUrl(restoredState.nextUrl || initEventsFetchUrl);
  //     }
  //   });
  // }, []);

  // const handleNavigation = () => {
  //   // Save state and scroll before navigating away
  //   saveStateAndScroll(window.location.pathname, {
  //     finishedEvents,
  //     artNameTerm,
  //     nextUrl,
  //   });
  //   navigate('/some-other-path'); // Example navigation
  // };

  const [fetching, setFetching] = useState(false);

  const [curUser, setCurUser] = useState(null); // some other user
  const fetchCurUser = () => {
    axiosInstance
    .get(`/users/${effectiveUserId()}`)
    .then((response) => {
      setCurUser(response.data);
    })
    .catch((error) => {
      console.error(error);      
    });
  }

  const fetchFollowCounts = () => {
    const targetUserId = effectiveUserId();
    if (!targetUserId) {
      setFollowersCount(null);
      setFolloweesCount(null);
      return;
    }

    Promise.all([
      axiosInstance.get(`/getUsers?onlyMyFollowers=true&forUserId=${targetUserId}`),
      axiosInstance.get(`/getUsers?onlyMyFollowees=true&forUserId=${targetUserId}`)
    ])
    .then(([followersResponse, followeesResponse]) => {
      setFollowersCount(followersResponse.data.length);
      setFolloweesCount(followeesResponse.data.length);
    })
    .catch((error) => {
      console.error(error);
    });
  };

  const [relevantTags, setRelevantTags] = useState(null);
  const fetchRelevantTags = (alreadySetTagIds=[]) => {
    const tagParams = alreadySetTagIds.map((n) => { return "alreadySetTags="+n; }).join("&");
    axiosInstance
    .get(`/relevant/tags?userId=${effectiveUserId()}&type=${eventType}&${tagParams}`)
    .then((response) => {
      setRelevantTags(response.data);
    })
    .catch((error) => {
      console.error(error);
    })
  }

  const [showTags, setShowTags] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);

  const modalRef = useRef(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const preventDefault = (e) => {
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.touches[0].clientX - rect.left,
      y: e.touches[0].clientY - rect.top,
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const modal = modalRef.current;
    const deltaX = e.touches[0].clientX - dragOffset.x;
    const deltaY = e.touches[0].clientY - dragOffset.y;
    if (deltaX < 0) {
      return; // Prevent dragging to the left
    }

    // Enable drag only if deltaX > deltaY
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Disable scrolling.
      modal.style.overflow = 'hidden';
      modal.addEventListener('touchmove', preventDefault, { passive: false });

      modal.style.transform = `translate(${deltaX}px, 0)`;

      // Reduce width and height proportionally, scale more slowly
      const scale = 1 - Math.min(deltaX / (window.innerWidth * 2), 0.1); // Limit reduction to 10%
      modal.style.transform += ` scale(${scale})`;

      // Enable vertical drag after horizontal drag
      if (Math.abs(deltaX) > 70) {
        modal.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scale})`;
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);

    // Enable scrolling again.
    modalRef.current.style.overflow = 'auto';
    modalRef.current.removeEventListener('touchmove', preventDefault);

    const rect = modalRef.current.getBoundingClientRect();
    const modalWidth = rect.width;

    // Close modal if dragged to the right by at least 20% of its width
    if (rect.left - dragOffset.x > modalWidth / 5) {
      setSelectedItem(null);
    } else {
      // Reset position if not dragged far enough
      modalRef.current.style.transform = "translate(0, 0)";
    }
  };

  const fetchFinishedEvents = useCallback((nextUrl, fromScratch = false) => {
    if (!nextUrl || (loading && !fromScratch)) {
      return;
    }
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setLoading(true);
    axiosInstance
    .get(nextUrl)
    .then((response) => {
      if (latestRequestIdRef.current !== requestId) {
        return;
      }
      if (finishedEvents === null || fromScratch) {
        setFinishedEvents(response.data.data);
      } else {
        setFinishedEvents(prevObjects => [...prevObjects, ...response.data.data]);
      }
      setNextUrl(response.data.nextPageUrl);
    })
    .catch((error) => {
      if (latestRequestIdRef.current !== requestId) {
        return;
      }
      if (error.response) {
        // Server responded with a status other than 200 range
        if (error.response.status === 403 || error.response.status === 401) {
          // Handle 403 Forbidden
          setHasAccess(false);
          console.log("Setting has access to false");
        } else {
          console.error('Error: ', error.response.data);
        }
      } else {
        console.error(error);  
      }
    })
    .finally(() => {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
      }
    });
  }, [finishedEvents, loading]);

  const handleFinishedInputChange = (event) => {
    setFinishedInput(event.target.value);
  };
  const handleFinished = () => {
    axiosInstance
    .post(`/artEvents`, { wikiArticleLink: finishedInput, type: eventType })
    .then((response) => {
      if (finishedEvents === null) {
        setFinishedEvents(response.data);
      } else {
        setFinishedEvents(prevObjects => [response.data, ...prevObjects]);  
      }
      setFinishedInput("");
    })
    .catch((error) => {
      console.error(error);
    });
  };

  // Gate the observer's first fetch so the initial page-2 pre-fetch
  // doesn't race the poster-image downloads and hurt LCP. Flip ready
  // on whichever happens first: user scrolls (they want more), or a
  // short delay after the grid painted (LCP had its chance).
  const [readyForMore, setReadyForMore] = useState(false);
  const readyTimerStartedRef = useRef(false);
  useEffect(() => {
    setReadyForMore(false);
    readyTimerStartedRef.current = false;
  }, [pathUserId, eventType]);
  useEffect(() => {
    if (finishedEvents === null || readyTimerStartedRef.current) return undefined;
    readyTimerStartedRef.current = true;
    const timer = setTimeout(() => setReadyForMore(true), 1500);
    const onScroll = () => { clearTimeout(timer); setReadyForMore(true); };
    window.addEventListener('scroll', onScroll, { once: true, passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [finishedEvents]);

  const lastObjectRef = useCallback(node => {
    if (loading) return;

    if (observer.current) {
      observer.current.disconnect();
    }
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && readyForMore) {
        fetchFinishedEvents(nextUrl);
      }
    });
    if (node) {
      observer.current.observe(node);
    }
  }, [loading, nextUrl, fetchFinishedEvents, readyForMore]);

  const curUserIsMe = () => {
    return (myUser && effectiveUserId() === myUser.myUserInfo.myUserId);
  }

  const isMobileDeviceExceptAndroid = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iphone|ipad|ipod|windows phone|blackberry/i.test(userAgent);
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
  };

  const isMobile = () => window.innerWidth <= 768;

  const handleItemClick = (item) => {
    if (isMobile()) {
      setSelectedItem(item); // Open modal on mobile
    } else {
      navigate(`/arts/${item.art.id}`); // Navigate on desktop
    }
  };

  const getGlobalManualTag = (eventTags) => {
    return eventTags?.find(tag => tag.type === 3)?.name || "like";
  };

  const handleGlobalManualTagSelect = (item, selectedTag) => {
    if (!item?.event?.id) {
      return;
    }
    const existingTags = item.event.eventTags?.map((tag) => tag.name) || [];
    const updatedTags = [
      selectedTag,
      ...existingTags.filter((tag) => !globalManualTags.includes(tag)),
    ];

    axiosInstance
      .post(`/artEvents/tag/${item.event.id}/tag`, { tags: updatedTags })
      .then((response) => {
        const updatedEvent = response.data.event;
        setFinishedEvents((prevEvents) => {
          if (!prevEvents) {
            return prevEvents;
          }
          return prevEvents.map((prevItem) =>
            prevItem.event.id === updatedEvent.id
              ? { ...prevItem, event: updatedEvent }
              : prevItem
          );
        });
        setOpenGlobalManualTagEventId(null);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const initialFetchDoneRef = useRef(false);
  useEffect(() => {
    if (!hasAccess || finishedEvents !== null || curUser !== null || fetching) {
      return;
    }
    if (initialFetchDoneRef.current) {
      return;
    }
    initialFetchDoneRef.current = true;
    setFetching(true);
    fetchFinishedEvents(nextUrl);
    fetchCurUser();
    fetchFollowCounts();
    fetchRelevantTags(alreadySelectedTagIds);
  }, [
    hasAccess,
    finishedEvents,
    curUser,
    fetching,
    fetchFinishedEvents,
    nextUrl,
    alreadySelectedTagIds,
  ]);

  if (hasAccess && (finishedEvents === null || curUser === null)) {
    return (
      <div></div>
    )
  }

  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const followersLabel = language === "ru" ? "Подписчики" : "Followers";
  const followeesLabel = language === "ru" ? "Подписки" : "Followings";
  const followersText = followersCount !== null ? `${followersLabel} (${followersCount})` : followersLabel;
  const followeesText = followeesCount !== null ? `${followeesLabel} (${followeesCount})` : followeesLabel;

  let buttonTextLocalizeKey = "addFinished";
  if (eventType === "like") {
    buttonTextLocalizeKey = "like";
  } else if (eventType === "todo") {
    buttonTextLocalizeKey = "addTodo";
  }

  // console.log("finishedEvents[0].event.chatId: " + finishedEvents[0].event.chatId);

  // Links to other types of events: start
  // TODO: move it to a separate user page component.
  const likesLink = `/users/${effectiveUserId()}/likes`;
  const classLikes = (eventType === "like" ? "underline" : "");

  const todosLink = `/users/${effectiveUserId()}/todos`;
  const classTodos = (eventType === "todo" ? "underline" : "");

  const finishedLink = `/users/${effectiveUserId()}/finished`;
  const classFinished = (eventType === "finished" ? "underline" : "");

  // Links to other types of events: end

  return (
    <>
    {curUser && effectiveUserId() && (
      <PageHeader
        title={curUser.userName + (myUser && myUser.myUserInfo.myUserId === curUser.id ? " (me)" : "")}
        rightSlot={(
          <div className="relative" data-page-menu>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setPageMenuOpen(v => !v); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPageMenuOpen(v => !v); } }}
              className="cursor-pointer select-none px-2"
              style={{ fontSize: 22, lineHeight: 1 }}
              aria-label={language === "ru" ? "Меню" : "Menu"}
            >
              &#x22EF;
            </div>
            {pageMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 rounded shadow-lg"
                style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', zIndex: 60 }}
              >
                <div
                  className="px-4 py-2 cursor-pointer text-sm"
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--mantine-color-default-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                  onClick={() => { handleCopyLink(); setPageMenuOpen(false); }}
                >
                  {language === "ru"
                    ? (isCopyLinkCopied ? "Ссылка скопирована ✔" : "Скопировать ссылку")
                    : (isCopyLinkCopied ? "Link copied ✔" : "Copy link")}
                </div>
              </div>
            )}
          </div>
        )}
      />
    )}
    <div ref={tabsSwipeRef} className="container mx-auto px-1 md:px-2 w-full lg:w-3/4">
      {/* <div className="container mx-auto">
        {
          myUser && effectiveUserId() !== myUser.myUserInfo.myUserId &&
            <TextUserPair user={curUser} userId={effectiveUserId()} hideRecomInput={true}/>
        }        
      </div> */}
      {
      (hasAccess === false) ?
        <div className="flex justify-center text-center">
          {language === "ru" ? "Приватный аккаунт, подпишитесь на пользователя и дождитесь подтверждения" : "Account is private, subscribe to user and wait for approval"}
        </div>
        :
          <>
            {/* App-recs ↔ Friends-recs switch (only when viewing your own
                incoming recommendations, mirroring the switch on the
                PlatformRecommendations page). */}
            {
            (eventType === 'like') &&
            <div>
              {/*<div className="flex justify-between items-center mt-2 mb-1 px-2">
                <div className="flex justify-center items-center space-x-2">
                  <Link to={"/logbookHistory?userId="+effectiveUserId()}>
                      <div className="container mx-auto flex justify-center items-center">
                      <div className="flex group-hover:hidden">
                        <img className="mt-0.5 max-w-9 max-h-9" src="/calendar_icon_2.png" alt="Calendar" style={{ filter: 'var(--icon-filter)' }} />
                      </div>
                    </div>
                  </Link>
                  {
                  (myUser && effectiveUserId() === myUser.myUserInfo.myUserId) &&
                    <Link to="/settings">
                      <img
                        src="/settings_icon.png"
                        alt={language === "ru" ? "Настройки" : "Settings"}
                        className="w-9 h-9 cursor-pointer"
                        style={{ filter: 'var(--icon-filter)' }}
                      />
                    </Link>
                  }
                </div>
              </div> */}
              <div>
                <Paper radius="md" p="sm" shadow="xs" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                  <Stack gap="sm">
                    <Group gap="sm" align="center" wrap="wrap">
                      <Group gap="sm" align="center" wrap="wrap">
                        <TextUserPair user={curUser} hideRecomInput={true} hideUserName={true} />
                        {(myUser && effectiveUserId() === myUser.myUserInfo.myUserId) && (
                          <ActionIcon
                            component={Link}
                            to="/settings"
                            variant="light"
                            size="lg"
                            radius="md"
                            aria-label={language === "ru" ? "Настройки" : "Settings"}
                          >
                            <Image src="/settings_icon.png" alt={language === "ru" ? "Настройки" : "Settings"} w={18} h={18} fit="contain" style={{ filter: 'var(--icon-filter)' }} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>

                    <Group justify="space-between" align="center" wrap="wrap" w="100%">
                      <Group gap="sm" wrap="wrap">
                        {myUser && (
                          <>
                            <Button
                              component={Link}
                              to={`/users?onlyMyFollowers=true&forUserId=${effectiveUserId()}`}
                              variant="light"
                              size="sm"
                              radius="md"
                            >
                              {followersText}
                            </Button>
                            <Button
                              component={Link}
                              to={`/users?onlyMyFollowees=true&forUserId=${effectiveUserId()}`}
                              variant="light"
                              size="sm"
                              radius="md"
                            >
                              {followeesText}
                            </Button>
                          </>
                        )}
                        {curUserIsMe() && !!myUser?.myUserInfo?.canDirectChat && (
                          <Button
                            component={Link}
                            to={`/chats`}
                            variant="light"
                            size="sm"
                            radius="md"
                          >
                            {language === "ru" ? "Чаты" : "Chats"}
                          </Button>
                        )}
                        {(() => {
                          const me = myUser?.myUserInfo?.myUserId;
                          const target = effectiveUserId();
                          if (!me || !target || me === target) return null;
                          const meCanChat = !!myUser?.myUserInfo?.canDirectChat;
                          const targetCanChat = !!curUser?.canDirectChat;
                          if (!meCanChat && !targetCanChat) return null;
                          return (
                            <Button
                              variant="light"
                              size="sm"
                              radius="md"
                              loading={openingDirectChat}
                              onClick={() => openDirectChatWith(target)}
                            >
                              {language === "ru" ? "Чат" : "Chat"}
                            </Button>
                          );
                        })()}
                      </Group>
                    </Group>

                    {(myUser && curUser.id !== myUser.myUserInfo.myUserId && curUser.matchScore) && (() => {
                      const pct = Math.floor(curUser.matchScore * 100);
                      const pctColor = pct >= 70 ? 'var(--mantine-color-green-4)'
                        : pct >= 40 ? 'var(--mantine-color-yellow-4)'
                        : 'var(--mantine-color-orange-4)';
                      return (
                      <Text size="sm" c="dimmed" lh="sm">
                        <Text component="span" fw={700} size="lg" style={{ color: pctColor, marginRight: 6 }}>{pct}%</Text>
                        {language === "ru" ? "совпадение вкусов" : "match of tastes"}
                        {(() => {
                          const parts = [];
                          if (curUser.matchMoviesCount > 0) parts.push(language === "ru" ? `${curUser.matchMoviesCount} фильмов` : `${curUser.matchMoviesCount} movies`);
                          if (curUser.matchBooksCount > 0) parts.push(language === "ru" ? `${curUser.matchBooksCount} книг` : `${curUser.matchBooksCount} books`);
                          if (curUser.matchGamesCount > 0) parts.push(language === "ru" ? `${curUser.matchGamesCount} игр` : `${curUser.matchGamesCount} games`);
                          if (parts.length > 0) return " | " + parts.join(", ");
                          return "";
                        })()}
                        {curUser.matchCreatedTime && (() => {
                          const diffMs = Date.now() - new Date(curUser.matchCreatedTime).getTime();
                          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                          const days = Math.floor(diffHours / 24);
                          const hours = diffHours % 24;
                          let ago;
                          if (language === "ru") {
                            const timeParts = [];
                            if (days > 0) timeParts.push(`${days} дн.`);
                            timeParts.push(`${hours} ч.`);
                            ago = timeParts.join(" ");
                          } else {
                            const timeParts = [];
                            if (days > 0) timeParts.push(`${days}d`);
                            timeParts.push(`${hours}h`);
                            ago = timeParts.join(" ");
                          }
                          return ` | ${language === "ru" ? "обновлено" : "updated"} ${ago} ${language === "ru" ? "назад" : "ago"}`;
                        })()}
                      </Text>
                      );
                    })()}
                  </Stack>
                </Paper>
              </div>
            </div>
            }

            {
            showLibraryContent && (finishedEvents && finishedEvents.length == 0) && artNameTerm === '' && !loading &&
              <div className="mt-5 justify-center text-center">
                {
                language === "ru"
                ?                  
                  (myUser && effectiveUserId() === myUser.myUserInfo.myUserId)
                  ?
                    eventType.includes('recommendation')
                    ?
                      <>
                        
                      </>
                    :
                      <>
                        Вы пока не добавили ни одного произведения в свою библиотеку.
                        Перейдите в поиск <img className="inline max-w-6 max-h-6" src="/add_icon.png" style={{ filter: 'var(--icon-filter)' }} /> и добавьте произведения.
                      </>
                  :
                    <>
                      Пользователь пока не добавил ни одного произведения в свою библиотеку.
                    </>
                :
                  (myUser && effectiveUserId() === myUser.myUserInfo.myUserId)
                  ?                    
                    eventType.includes('recommendation')
                    ?
                      <>
                        No personal recommendations yet.
                      </>
                    :
                      <>
                        You have not added any arts yet to your library.
                        Go to search <img className="inline max-w-6 max-h-6" src="/add_icon.png" style={{ filter: 'var(--icon-filter)' }} /> and add arts.
                      </>                    
                  :
                    <>
                      The user has not added any arts yet to their library.
                    </>
                }
              </div>
            }

            {
            (finishedEvents && finishedEvents.length > 0 || artNameTerm.length > 0 || eventType.includes('recommendation') || eventType === 'like') &&
            <div className={"rounded-md border-dotted left-0 w-full z-50 justify-center" + (myUser ? " bottom-20" : " bottom-0")} style={{ borderColor: "var(--mantine-color-default-border)" }}>

              <div className="flex justify-center items-center space-x-4">
                {
                myUser && effectiveUserId() === myUser.myUserInfo.myUserId &&
                  (
                    eventType.includes('recommendation')
                    &&
                      <div>
                        <div className="flex justify-center items-center text-center space-x-4">
                          {onEventTypeChange ? (
                            <>
                              <Button
                                variant="light"
                                size="sm"
                                radius="md"
                                className={eventType === "recommendationIncoming" ? "underline" : ""}
                                onClick={() => onEventTypeChange("recommendationIncoming")}
                              >
                                {language === "ru" ? "Мне" : "To me"}
                              </Button>
                              <Button
                                variant="light"
                                size="sm"
                                radius="md"
                                className={eventType === "recommendationOutgoing" ? "underline" : ""}
                                onClick={() => onEventTypeChange("recommendationOutgoing")}
                              >
                                {language === "ru" ? "От меня" : "From me"}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Link to={`/users/${effectiveUserId()}/recommendationsIncoming`}>
                                <Button
                                  variant="light"
                                  size="sm"
                                  radius="md"
                                  className={eventType === "recommendationIncoming" ? "underline" : ""}
                                >
                                  {language === "ru" ? "Мне" : "To me"}
                                </Button>
                              </Link>
                              <Link to={`/users/${effectiveUserId()}/recommendationsOutgoing`}>
                                <Button
                                  variant="light"
                                  size="sm"
                                  radius="md"
                                  className={eventType === "recommendationOutgoing" ? "underline" : ""}
                                >
                                  {language === "ru" ? "От меня" : "From me"}
                                </Button>
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                  )                
                }
              </div>

              <div className="flex justify-center items-center mt-6 mb-2">
                <div className="flex font-[sans-serif]">
                  <TextInput
                    ref={searchInputRef}
                    type="search"
                    className="w-64"
                    value={artNameTerm}
                    onChange={(event) => {
                      const nextTerm = event.currentTarget.value;
                      const eventsFetchUrl = `/artEvents/${effectiveUserId()}/${eventType}?${window.location.search}&artNameTerm=${nextTerm}`;
                      fetchFinishedEvents(eventsFetchUrl, true);

                      setArtNameTerm(nextTerm); // to intercept in tags selection
                      syncSearchTermToURL(nextTerm);
                      setShouldFocus(true);
                    }}
                    placeholder={language === "ru" ? ( (!myUser || effectiveUserId() !== myUser.myUserInfo.myUserId) ? "Поиск в библиотеке пользователя" : "Поиск в моей библиотеке") : ( (!myUser || effectiveUserId() !== myUser.myUserInfo.myUserId) ? "Search in user's library" : "Search in my library")}
                    leftSection={
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.904 192.904" width="16px"
                        className="rotate-120" style={{ fill: "var(--mantine-color-dimmed)" }}>
                        <path
                          d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z">
                        </path>
                      </svg>
                    }
                    rightSection={
                      (isMobileDeviceExceptAndroid() || isFirefox()) && artNameTerm ? (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() => {
                            const eventsFetchUrl = `/artEvents/${effectiveUserId()}/${eventType}?${window.location.search}&artNameTerm=`;
                            fetchFinishedEvents(eventsFetchUrl, true);

                            setArtNameTerm('');
                            syncSearchTermToURL('');
                            setShouldFocus(true);
                            if (searchInputRef.current) {
                              searchInputRef.current.focus();
                            }
                          }}
                          aria-label={language === "ru" ? "Очистить поиск" : "Clear search"}
                        >
                          &times;
                        </ActionIcon>
                      ) : null
                    }
                    styles={{
                      input: {
                        backgroundColor: "var(--mantine-color-default)",
                        color: "var(--mantine-color-text)",
                      },
                      section: { color: "var(--mantine-color-dimmed)" }
                    }}
                    radius="xl"
                  />
                </div>
                {/* {
                (finishedEvents && finishedEvents.length > 5) &&
                  <div className="ml-2 text-4xl leading-none transform translate-y-[-2px] hover:cursor-pointer hover:text-gray-400" onClick={toggleGridCols}>
                    ⊞
                  </div>
                } */}
                {/*
                <div className="text-3xl">
                  {
                    eventType === "like" &&
                    <ShareButton
                      url={
                        window.location.href
                      }
                      text={
                        ""
                      }
                      overrideButtonText="🔗"
                    />
                  }
                </div>
                */}
              </div>
              {eventType === 'like' && (
                <div className="flex justify-center mt-2 mb-1">
                  <Tabs value={tab} onChange={setTabAndUrl} variant="default">
                    <Tabs.List>
                      <Tabs.Tab value="library">
                        {language === 'ru' ? 'Библиотека' : 'Library'}
                      </Tabs.Tab>
                      <Tabs.Tab value="logbook">
                        {language === 'ru' ? 'Журнал' : 'Logbook'}
                      </Tabs.Tab>
                      <Tabs.Tab value="reviews">
                        {language === 'ru' ? 'Отзывы' : 'Reviews'}
                      </Tabs.Tab>
                    </Tabs.List>
                  </Tabs>
                </div>
              )}
              {showLibraryContent &&
              // showTags &&
              <div className="mb-5">
                <SelectableTags language={language} relevantTags={relevantTags !== null ? relevantTags : []} alreadySelectedTags={ relevantTags ? relevantTags.filter((t) => alreadySelectedTagIds.includes(t.tag.id)) : []} onSelectionChange={(tags) => {
                  const tagParams = tags.map((t) => { return "tags="+t.tag.id; }).join("&");

                  const url = window.location.origin;
                  // const url = 'http://localhost:8080'
                  let urlEventType = "likes";
                  if (eventType === "recommendationIncoming") {
                    urlEventType = "recommendationsIncoming";
                  } else if (eventType === "recommendationOutgoing") {
                    urlEventType = "recommendationsOutgoing";
                  }
                  // Preserve other query params (notably ?q= for the search term) while replacing tags.
                  const nextParams = new URLSearchParams(window.location.search);
                  nextParams.delete('tags');
                  tags.forEach((t) => nextParams.append('tags', t.tag.id));
                  const nextSearch = nextParams.toString();
                  window.history.pushState({}, '', `${url}/users/${curUser.id}/${urlEventType}${ nextSearch ? "?"+nextSearch : "" }`);

                  const eventsFetchUrl = `/artEvents/${effectiveUserId()}/${eventType}?${tagParams}&artNameTerm=${artNameTerm}`;
                  fetchFinishedEvents(eventsFetchUrl, true);

                  const tagIds = tags.map((t) => { return t.tag.id; });
                  fetchRelevantTags(tagIds);
                }} />
              </div>
              }
            </div>
            }
            {/* Grid */}
            {showLibraryContent && (() => {
              const cols = window.innerWidth >= 768 ? 6 : 4;
              const elements = [];
              for (let i = 0; i < finishedEvents.length; i += cols) {
                const rowItems = finishedEvents.slice(i, i + cols);
                const selectedInRow = selectedGridItemIdx !== null && selectedGridItemIdx >= i && selectedGridItemIdx < i + cols;
                const selectedItem = selectedInRow ? finishedEvents[selectedGridItemIdx] : null;
                const detail = selectedItem ? gridArtDetails[selectedItem.art.id] : null;

                elements.push(
                  <div key={`row-${i}`} className="grid grid-cols-4 md:grid-cols-6 gap-1.5 mb-1.5">
                    {rowItems.map((item, j) => {
                      const idx = i + j;
                      return (
                        <div
                          key={idx}
                          ref={idx === finishedEvents.length - 1 ? lastObjectRef : null}
                          className="cursor-pointer"
                          onClick={() => handleSelectGridItem(idx)}
                        >
                          <div className="relative">
                            <ArtThumbnail art={item.art} displayCheckmark={eventType !== "like"} displayTag={false} />
                            {eventType.includes('like') && getGlobalManualTag(item.event.eventTags) && (() => {
                              const gt = getGlobalManualTag(item.event.eventTags);
                              return (
                                <div
                                  className="absolute top-0.5 right-0.5 rounded-full flex items-center justify-center text-sm leading-none"
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: 'rgba(0,0,0,0.55)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255,255,255,0.18)',
                                  }}
                                >
                                  {gt === 'like' ? (
                                    <img src="/heart_icon_5.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : gt === 'veryGood' ? (
                                    <img src="/fire_icon_2.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : gt === 'good' ? (
                                    <img src="/star_icon.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : gt === 'finished' ? (
                                    <img src="/white_circle_icon.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : gt === 'todo' ? (
                                    <img src="/todo_pin_icon.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : gt === 'inProgress' ? (
                                    <img src="/sand_clock_icon.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : gt === 'bad' ? (
                                    <img src="/thumbs_down_icon.png" alt="" style={{ width: 16, height: 16 }} />
                                  ) : tagName(gt, language).split(' ')[0]}
                                </div>
                              );
                            })()}
                          </div>
                          {/* Title underlay — always visible, truncated to one
                              line. Thumbnails arrive at inconsistent sizes
                              and some posters have no title typography; this
                              gives every tile a legible identifier even when
                              the poster is tiny or broken. */}
                          {(() => {
                            const localized = localizeArt(item.art, language);
                            const name = localized?.name || item.art.name || '';
                            if (!name) return null;
                            return (
                              <div
                                className="mt-1 px-0.5 text-[11px] leading-tight truncate"
                                style={{ color: 'var(--mantine-color-text)' }}
                                title={name}
                              >
                                {name}
                              </div>
                            );
                          })()}
                          {eventType.includes('recommendation') && (
                            <div className="flex items-center justify-center -space-x-2 mt-1">
                              {(item.event.eventTags || [])
                                .filter((tag) => tag.imgUrl)
                                .slice(0, 5)
                                .map((tag, avatarIndex) => (
                                  <img
                                    key={`${tag.imgUrl}-${avatarIndex}`}
                                    src={tag.imgUrl}
                                    alt={tag.name || "avatar"}
                                    className="w-6 h-6 rounded-full"
                                    style={{ border: "1px solid var(--mantine-color-default-border)", backgroundColor: "var(--mantine-color-default)" }}
                                  />
                                ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );

                if (selectedInRow && selectedItem) {
                  const desc = detail || {};
                  const localized = localizeArt(selectedItem.art, language);
                  const description = language === 'ru'
                    ? (desc.ruDescription || desc.description)
                    : (desc.description || desc.ruDescription);
                  const genres = selectedItem.art.genres || [];
                  const author = selectedItem.art.author || '';
                  elements.push(
                    <Paper key={`detail-${i}`} ref={scrollDetailIntoView} shadow="xs" radius="md" className="mb-2 p-1.5" style={{ backgroundColor: 'var(--mantine-color-body)' }}>
                      <div className="flex gap-3">
                        <Link to={`/arts/${selectedItem.art.id}`}>
                          <img src={selectedItem.art.thumbnailUrl} alt="" className="w-[83px] h-auto object-cover rounded-md" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <Link to={`/arts/${selectedItem.art.id}`}>
                              <Text size="sm" fw={600}>{localized.name + formatYear(selectedItem.art.year, language)}</Text>
                            </Link>
                            {eventType !== 'like' && (
                              <Link to={`/artEvents/${selectedItem.event.id}`} className="flex items-center flex-shrink-0">
                                <img
                                  src="/chat_icon.png"
                                  alt=""
                                  className="opacity-80"
                                  style={{ width: 24, height: 24, filter: 'var(--icon-filter)' }}
                                />
                                {selectedItem.chat?.messageCount > 0 && (
                                  <Text component="span" className="ml-1" size="xs" c="dimmed">
                                    {selectedItem.chat.messageCount}
                                  </Text>
                                )}
                              </Link>
                            )}
                          </div>
                          {author && (
                            <Text size="xs" c="dimmed" className="mt-0.5">
                              {selectedItem.art.category === 1
                                ? (language === 'ru' ? 'Режиссёр' : 'Director')
                                : selectedItem.art.category === 2
                                  ? (language === 'ru' ? 'Разработчик' : 'Developer')
                                  : (language === 'ru' ? 'Автор' : 'Author')
                              }: {author}
                            </Text>
                          )}
                          {genres.length > 0 && (
                            <Text size="xs" c="dimmed" className="mt-0.5">
                              {localizeGenres(genres, language).join(', ')}
                            </Text>
                          )}
                          <div className="mt-1">
                            <PlatformRatings art={selectedItem.art} center={false} showVotes={true} />
                          </div>
                          {description && (
                            <Text size="xs" className="mt-1" style={{ lineHeight: 1.4 }}>
                              {description}
                            </Text>
                          )}
                        </div>
                      </div>
                      {/* Pill is a Paper sibling of the (thumb + info)
                          flex row so justify-center centers it against
                          the full Paper width, not just the right
                          column beside the thumb. */}
                      {eventType.includes('like') && (
                        <div className="mt-4 flex justify-center">
                          <CardEventArtChatMini
                            art={{ ...selectedItem.art, myEventId: selectedItem.event.id }}
                            readOnly={!curUserIsMe()}
                            fullWidth
                            libraryLinkText={
                              curUserIsMe()
                                ? (language === 'ru' ? 'Перейти в мою библиотеку' : 'Go to my library')
                                : (language === 'ru'
                                  ? `Перейти в библиотеку ${curUser?.userName || ''}`.trim()
                                  : `Go to ${curUser?.userName || ''}'s library`)
                            }
                          />
                        </div>
                      )}
                    </Paper>
                  );
                }
              }
              return elements;
            })()}
            {/* Pre-warm the Logbook fetch so clicking that tab is
                instant — but only after the artEvents grid has painted,
                otherwise the 330KB logbook response races the poster
                images and slows LCP. */}
            {eventType === 'like' && (tab === 'logbook' || logbookPrewarmReady) && (
              <div style={{ display: tab === 'logbook' ? 'block' : 'none' }}>
                <LogbookHistory userId={effectiveUserId()} embedded />
              </div>
            )}
            {eventType === 'like' && tab === 'reviews' && (
              <UserReviewsList
                userId={effectiveUserId()}
                initialItems={reviewsCache}
                onLoaded={setReviewsCache}
              />
            )}
            {/* <button onClick={handleNavigation}>Navigate</button> */}

          </>
      }

    </div>
    </>
  );
}

export default PersonalArtEvents;
